import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import calendarIcon from 'assets/icons/calendar.svg';
import downloadIcon from 'assets/icons/download.svg';
import { isCanceledError } from 'modules/api/request';
import { getExporterJobDownloadUrl } from 'modules/api/inventory';
import { formatDateCustom } from 'modules/I18N';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { downloadFromUrl } from 'utils/download';
import SvgIcon from 'lib/SvgIcon';
import Chip, { Size } from 'lib/Chip';
import IconButton from 'lib/IconButton';
import Card, { CardTheme } from 'lib/Card';
import { Theme } from 'lib/IconButton/IconButton.types';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import { CircularLoadingSpinner, SpinnerSize } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import { getLocaleCode, getMarketId } from 'store/publisher/selectors';
import { ExportJob, ExportJobStatusColor, ExportJobStatusName } from '../types/ExportJob.types';

interface Props {
  job: ExportJob;
}

export const ExportJobCard = ({ job }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const localeCode = useSelector(getLocaleCode);
  const market = useSelector(getMarketId);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);

  const formattedCreatedDate = formatDateCustom(
    job.createdDate,
    {
      dateStyle: 'long',
      timeZone: 'UTC',
    },
    localeCode,
  );

  const onDownloadJobClick = async (): Promise<void> => {
    setIsGeneratingUrl(true);

    try {
      const { url, fileName } = await getExporterJobDownloadUrl(market, job.jobId, cancelFunctions);

      downloadFromUrl(url, fileName);
    } catch (error) {
      if (isCanceledError(error)) return;

      dispatch(
        addToastNotification({
          header: 'Download export',
          description: 'Failed downloading export. Please try again later.',
          type: 'Failure',
          timeout: 10000,
        }),
      );
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  return (
    <Card theme={CardTheme.TRANSPARENT_WITH_BORDER}>
      <div className="p-1.5 flex flex-row justify-between items-center space-x-2">
        <div className="py-0.5 flex flex-row justify-between items-center space-x-2">
          <SvgIcon src={calendarIcon} className="w-5 h-5" />
          <p className="whitespace-nowrap sub-header-base">{formattedCreatedDate}</p>
        </div>
        <Tooltip tooltip={job.fileName} direction={TooltipDirection.TOP} size={TooltipSize.FULL}>
          <p className="sub-header-base text-neutral-600 truncate">{job.fileName}</p>
        </Tooltip>
        <div className="flex flex-row items-center space-x-2">
          <div>
            <Chip label={ExportJobStatusName[job.status]} color={ExportJobStatusColor[job.status]} size={Size.MEDIUM} />
          </div>
          {['PROCESSING', 'COMPLETE'].includes(job.status) && (
            <div className="w-5 h-5 items-center">
              {(isGeneratingUrl || job.status === 'PROCESSING') && <CircularLoadingSpinner size={SpinnerSize.SMALL} />}
              {!isGeneratingUrl && job.status === 'COMPLETE' && (
                <IconButton
                  icon={downloadIcon}
                  name="download-job"
                  theme={Theme.TRANSPARENT}
                  handleOnClick={onDownloadJobClick}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
